import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { AppDispatch } from '../store/store';
import { getClinicFeaturesApi } from '../rtk-apis/clinics/getClinicFeatures';
import { getToken } from '../Utils/authUtils';

/**
 * Keeps the clinic's feature flags loaded for the whole app. Mounted once at the
 * router root, beside TokenRefreshManager.
 *
 * Why a component and not just the hook: the route guards read the flags from
 * storage synchronously, before any page has mounted, so something has to put
 * them there as soon as a session exists.
 *
 * It watches the router location rather than a token state variable because
 * logging in is a navigation — there is no re-render to observe otherwise. A ref
 * holds the token the flags were fetched for, so navigating around does not
 * refetch, but a different login on the same tab does. A super admin's mid-session
 * change reaches a clinician on their next reload; the server enforces it
 * immediately regardless of what this cache says.
 *
 * Renders nothing.
 */
const ClinicFeatureSync = () => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const fetchedForToken = useRef<string | null>(null);

  useEffect(() => {
    const token = getToken();
    if (!token) {
      // Logged out: forget what we fetched so the next login fetches again.
      fetchedForToken.current = null;
      return;
    }
    if (fetchedForToken.current === token) {
      return;
    }
    fetchedForToken.current = token;
    dispatch(getClinicFeaturesApi());
    // location is a dependency only as a cheap "something happened" signal —
    // the token comparison above is what actually decides whether to fetch.
  }, [location, dispatch]);

  return null;
};

export default ClinicFeatureSync;
